import type { BaseMessage } from "@langchain/core/messages";

import { DEFAULT_MESSAGES_KEY } from "../stream/constants.js";
import type { ThreadState, Interrupt } from "../schema.js";
import type { StreamMode } from "../types.stream.js";
import type { Message } from "../types.messages.js";
import type { BagTemplate } from "../types.template.js";
import { StreamManager, type EventStreamEvent } from "./manager.js";
import {
  MessageTupleManager,
  toMessageClass,
  ensureMessageInstances,
} from "./messages.js";
import {
  extractInterrupts,
  userFacingInterruptsFromValuesArray,
} from "./interrupts.js";
import { getToolCallsWithResults } from "../utils/tools.js";
import { flushPendingHeadlessToolInterrupts } from "../headless-tools.js";
import type {
  AnyStreamCustomOptions,
  CustomSubmitOptions,
  MessageMetadata,
  GetUpdateType,
  GetCustomEventType,
  GetInterruptType,
  GetConfigurableType,
  SubagentStreamInterface,
} from "./types.js";

/**
 * Create a custom transport thread state.
 * @param values - The values to use.
 * @param threadId - The ID of the thread to use.
 * @returns The custom transport thread state.
 */
function createCustomTransportThreadState<
  StateType extends Record<string, unknown>,
>(values: StateType, threadId: string): ThreadState<StateType> {
  return {
    values,
    next: [],
    tasks: [],
    metadata: undefined,
    created_at: null,
    checkpoint: {
      thread_id: threadId,
      checkpoint_id: null,
      checkpoint_ns: "",
      checkpoint_map: null,
    },
    parent_checkpoint: null,
  };
}

/**
 * Framework-agnostic orchestrator for custom transport streams.
 *
 * Encapsulates all business logic shared across React, Vue, Svelte, and Angular
 * for custom transport (non-LGP) streaming.
 */
export class CustomStreamOrchestrator<
  StateType extends Record<string, unknown> = Record<string, unknown>,
  Bag extends BagTemplate = BagTemplate,
> {
  readonly stream: StreamManager<StateType, Bag>;

  readonly messageManager: MessageTupleManager;

  readonly #options: AnyStreamCustomOptions<StateType, Bag>;

  readonly #historyValues: StateType;

  #threadId: string | null;

  #branch: string = "";

  #listeners = new Set<() => void>();

  #version = 0;

  #streamUnsub: (() => void) | null = null;

  #disposed = false;

  #handledHeadlessToolInterruptIds = new Set<string>();

  /**
   * Create a new {@link CustomStreamOrchestrator} instance.
   *
   * @param options - Configuration options for the custom transport stream,
   *   including thread ID, transport, callbacks, and subagent settings.
   */
  constructor(options: AnyStreamCustomOptions<StateType, Bag>) {
    this.#options = options;

    this.#threadId = options.threadId ?? null;

    this.messageManager = new MessageTupleManager();
    this.stream = new StreamManager<StateType, Bag>(this.messageManager, {
      throttle: options.throttle ?? false,
      subagentToolNames: options.subagentToolNames,
      filterSubagentMessages: options.filterSubagentMessages,
      toMessage: options.toMessage ?? toMessageClass,
    });

    this.#historyValues = options.initialValues ?? ({} as StateType);

    this.#streamUnsub = this.stream.subscribe(() => {
      this.#flushPendingHeadlessToolInterrupts();
      this.#notify();
    });

    const historyMessages = this.#getMessages(this.#historyValues);
    if (
      options.filterSubagentMessages &&
      !this.stream.isLoading &&
      historyMessages.length > 0
    ) {
      this.stream.reconstructSubagents(historyMessages, {
        skipIfPopulated: true,
      });
    }
  }

  /**
   * Register a listener that is called whenever the orchestrator state changes.
   *
   * @param listener - Callback invoked on each state change.
   * @returns An unsubscribe function that removes the listener.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /**
   * Return the current version number, incremented on each state change.
   * Useful as a cache key for external sync (e.g. `useSyncExternalStore`).
   *
   * @returns The current version counter.
   */
  getSnapshot = (): number => this.#version;

  /**
   * Custom transports do not negotiate stream modes with the server the way
   * LangGraph Platform streams do, but framework adapters call this method on
   * both orchestrator variants. Keep the API surface aligned as a no-op.
   */
  trackStreamMode(..._modes: StreamMode[]): void {}

  #notify(): void {
    if (this.#disposed) return;
    this.#version += 1;
    for (const listener of this.#listeners) {
      listener();
    }
  }

  /**
   * Synchronize the external thread ID with the orchestrator.
   * If the ID has changed, the current stream is cleared and listeners
   * are notified.
   *
   * @param newId - The new thread ID, or `null` to clear.
   */
  syncThreadId(newId: string | null): void {
    if (newId !== this.#threadId) {
      this.#threadId = newId;
      this.#handledHeadlessToolInterruptIds.clear();
      this.stream.clear();
      this.#notify();
    }
  }

  #getMessages = (value: StateType): Message[] => {
    const messagesKey = this.#options.messagesKey ?? DEFAULT_MESSAGES_KEY;
    return Array.isArray(value[messagesKey])
      ? (value[messagesKey] as Message[])
      : [];
  };

  #setMessages = (current: StateType, messages: Message[]): StateType => {
    const messagesKey = this.#options.messagesKey ?? DEFAULT_MESSAGES_KEY;
    return { ...current, [messagesKey]: messages };
  };

  /**
   * The current stream state values, falling back to an empty object
   * when no stream values are available.
   */
  get values(): StateType {
    return this.stream.values ?? ({} as StateType);
  }

  /**
   * The raw stream state values, or `null` if no stream has been started
   * or values have not yet been received.
   */
  get streamValues(): StateType | null {
    return this.stream.values;
  }

  /** The most recent stream error, or `undefined` if no error occurred. */
  get error(): unknown {
    return this.stream.error;
  }

  /** Whether a stream is currently in progress. */
  get isLoading(): boolean {
    return this.stream.isLoading;
  }

  /** The current branch identifier. */
  get branch(): string {
    return this.#branch;
  }

  /**
   * Update the current branch and notify listeners.
   *
   * @param value - The new branch identifier.
   */
  setBranch(value: string): void {
    this.#branch = value;
    this.#notify();
  }

  /**
   * All messages from the current stream values, converted to
   * {@link BaseMessage} instances. Returns an empty array when no
   * stream values are available.
   */
  get messages(): BaseMessage[] {
    if (!this.stream.values) return [];
    return ensureMessageInstances(
      this.#getMessages(this.stream.values)
    ) as BaseMessage[];
  }

  /**
   * All tool calls paired with their results extracted from the
   * current stream messages.
   */
  get toolCalls() {
    if (!this.stream.values) return [];
    return getToolCallsWithResults(this.#getMessages(this.stream.values));
  }

  /**
   * Get tool calls (with results) that belong to a specific AI message.
   *
   * @param message - The AI message whose tool calls to retrieve.
   * @returns Tool calls associated with the given message.
   */
  getToolCalls(message: Message) {
    if (!this.stream.values) return [];
    const allToolCalls = getToolCallsWithResults(
      this.#getMessages(this.stream.values)
    );
    return allToolCalls.filter((tc) => tc.aiMessage.id === message.id);
  }

  /**
   * All active interrupts from the current stream values.
   * Returns a single breakpoint interrupt when the interrupt array is
   * present but empty, or an empty array when no interrupts exist.
   */
  get interrupts(): Interrupt<GetInterruptType<Bag>>[] {
    if (
      this.stream.values != null &&
      "__interrupt__" in this.stream.values &&
      Array.isArray(this.stream.values.__interrupt__)
    ) {
      return userFacingInterruptsFromValuesArray<GetInterruptType<Bag>>(
        this.stream.values.__interrupt__ as Interrupt<GetInterruptType<Bag>>[]
      );
    }
    return [];
  }

  /**
   * The first active interrupt extracted from the current stream values,
   * or `undefined` if there are no interrupts.
   */
  get interrupt(): Interrupt<GetInterruptType<Bag>> | undefined {
    return extractInterrupts<GetInterruptType<Bag>>(this.stream.values);
  }

  /**
   * Retrieve stream-level metadata for a given message.
   *
   * @param message - The message to look up metadata for.
   * @param index - Optional positional index used as fallback message ID.
   * @returns The metadata associated with the message, or `undefined`
   *   if no stream metadata is available.
   */
  getMessagesMetadata(
    message: Message,
    index?: number
  ): MessageMetadata<StateType> | undefined {
    const streamMetadata = this.messageManager.get(message.id)?.metadata;
    if (streamMetadata != null) {
      return {
        messageId: message.id ?? String(index),
        firstSeenState: undefined,
        branch: undefined,
        branchOptions: undefined,
        streamMetadata,
      } as MessageMetadata<StateType>;
    }
    return undefined;
  }

  /** A map of all tracked subagent streams, keyed by tool call ID. */
  get subagents(): Map<string, SubagentStreamInterface> {
    return this.stream.getSubagents();
  }

  /** The subset of subagent streams that are currently active (loading). */
  get activeSubagents(): SubagentStreamInterface[] {
    return this.stream.getActiveSubagents();
  }

  /**
   * Look up a single subagent stream by its tool call ID.
   *
   * @param toolCallId - The tool call ID that initiated the subagent.
   * @returns The subagent stream, or `undefined` if not found.
   */
  getSubagent(toolCallId: string) {
    return this.stream.getSubagent(toolCallId);
  }

  /**
   * Retrieve all subagent streams matching a given tool name / type.
   *
   * @param type - The subagent type (tool name) to filter by.
   * @returns An array of matching subagent streams.
   */
  getSubagentsByType(type: string) {
    return this.stream.getSubagentsByType(type);
  }

  /**
   * Retrieve all subagent streams associated with a specific AI message.
   *
   * @param messageId - The ID of the parent AI message.
   * @returns An array of subagent streams linked to the message.
   */
  getSubagentsByMessage(messageId: string) {
    return this.stream.getSubagentsByMessage(messageId);
  }

  /**
   * Reconstruct subagent streams from history values when subagent
   * filtering is enabled and the stream is not currently loading.
   * This is a no-op if subagents are already populated.
   */
  reconstructSubagentsIfNeeded(): void {
    const hvMessages = this.#getMessages(this.#historyValues);
    if (
      this.#options.filterSubagentMessages &&
      !this.stream.isLoading &&
      hvMessages.length > 0
    ) {
      this.stream.reconstructSubagents(hvMessages, { skipIfPopulated: true });
    }
  }

  /**
   * Abort the current stream and invoke the `onStop` callback
   * if one was provided in the options.
   */
  stop(): void {
    void this.stream.stop(this.#historyValues, {
      onStop: this.#options.onStop,
    });
  }

  /**
   * Switch to a different thread. If the thread ID actually changed,
   * the current stream is cleared and listeners are notified.
   *
   * @param newThreadId - The thread ID to switch to, or `null` to clear.
   */
  switchThread(newThreadId: string | null): void {
    if (newThreadId !== this.#threadId) {
      this.#threadId = newThreadId;
      this.#handledHeadlessToolInterruptIds.clear();
      this.stream.clear();
      this.#notify();
    }
  }

  /**
   * Start a new stream run against the custom transport.
   *
   * This is the low-level submit entry point that handles thread ID
   * resolution, optimistic value merging, and transport invocation.
   * Prefer {@link submit} unless you need to bypass higher-level wrappers.
   *
   * @param values - The input values to send, or `null`/`undefined` for
   *   a resume-style invocation.
   * @param submitOptions - Optional per-call overrides such as
   *   `optimisticValues`, `config`, `command`, and error callbacks.
   */
  async submitDirect(
    values: GetUpdateType<Bag, StateType> | null | undefined,
    submitOptions?: CustomSubmitOptions<StateType, GetConfigurableType<Bag>>
  ): Promise<void> {
    type UpdateType = GetUpdateType<Bag, StateType>;
    type CustomType = GetCustomEventType<Bag>;

    const currentThreadId = this.#options.threadId ?? null;
    if (currentThreadId !== this.#threadId) {
      this.#threadId = currentThreadId;
      this.stream.clear();
    }

    let usableThreadId = this.#threadId ?? submitOptions?.threadId;

    this.stream.setStreamValues(() => {
      if (submitOptions?.optimisticValues != null) {
        return {
          ...this.#historyValues,
          ...(typeof submitOptions.optimisticValues === "function"
            ? submitOptions.optimisticValues(this.#historyValues)
            : submitOptions.optimisticValues),
        };
      }

      return { ...this.#historyValues };
    });

    await this.stream.start(
      async (signal: AbortSignal) => {
        if (!usableThreadId) {
          usableThreadId = crypto.randomUUID();
          this.#threadId = usableThreadId;
          this.#options.onThreadId?.(usableThreadId);
        }

        if (!usableThreadId) {
          throw new Error("Failed to obtain valid thread ID.");
        }

        return this.#options.transport.stream({
          input: values,
          context: submitOptions?.context,
          command: submitOptions?.command,
          streamSubgraphs: submitOptions?.streamSubgraphs,
          signal,
          config: {
            ...submitOptions?.config,
            configurable: {
              thread_id: usableThreadId,
              ...submitOptions?.config?.configurable,
            } as unknown as GetConfigurableType<Bag>,
          },
        }) as Promise<
          AsyncGenerator<EventStreamEvent<StateType, UpdateType, CustomType>>
        >;
      },
      {
        getMessages: this.#getMessages,
        setMessages: this.#setMessages,
        initialValues: {} as StateType,
        callbacks: this.#options,
        onSuccess: () => {
          if (!usableThreadId) return undefined;

          const finalValues = this.stream.values ?? this.#historyValues;
          this.#options.onFinish?.(
            createCustomTransportThreadState(finalValues, usableThreadId),
            undefined
          );

          return undefined;
        },
        onError: (error) => {
          this.#options.onError?.(error, undefined);
          submitOptions?.onError?.(error, undefined);
        },
      }
    );
  }

  /**
   * Submit input values and start a new stream run.
   *
   * Delegates to {@link submitDirect}. Override or wrap this method
   * in framework adapters to add queuing or other middleware.
   *
   * @param values - The input values to send, or `null`/`undefined` for
   *   a resume-style invocation.
   * @param submitOptions - Optional per-call overrides.
   */
  async submit(
    values: GetUpdateType<Bag, StateType> | null | undefined,
    submitOptions?: CustomSubmitOptions<StateType, GetConfigurableType<Bag>>
  ): Promise<void> {
    await this.submitDirect(values, submitOptions);
  }

  /**
   * Tear down the orchestrator. Marks the instance as disposed,
   * unsubscribes from the stream, and aborts any in-progress stream.
   * After calling this method, no further notifications will be emitted.
   */
  dispose(): void {
    this.#disposed = true;
    this.#streamUnsub?.();
    this.#streamUnsub = null;
    void this.stream.stop(this.#historyValues, {
      onStop: this.#options.onStop,
    });
  }

  #flushPendingHeadlessToolInterrupts(): void {
    flushPendingHeadlessToolInterrupts(
      this.stream.values as Record<string, unknown> | null,
      this.#options.tools,
      this.#handledHeadlessToolInterruptIds,
      {
        onTool: this.#options.onTool,
        defer: (run) => {
          void Promise.resolve().then(run);
        },
        resumeSubmit: (command) =>
          this.submit(null, {
            command,
          }),
      }
    );
  }
}
